(() => {
  if (customElements.get('trustpilot-cart-invitation')) return;

  const ATTRIBUTE_KEY = 'trustpilot_einladung';
  const VALID_VALUES = new Set(['ja', 'nein']);
  const instances = new Set();

  let initialized = false;
  let confirmedValue = null;
  let desiredValue = null;
  let saving = false;
  let initializationFailed = false;
  let cartToken = null;

  const checkoutButtons = () => document.querySelectorAll('[data-trustpilot-checkout]');

  const lockCheckout = () => {
    checkoutButtons().forEach((button) => {
      if (!button.hasAttribute('data-trustpilot-locked')) {
        const pendingInitialization = button.hasAttribute('data-trustpilot-pending-initialization');
        button.dataset.trustpilotWasDisabled = button.disabled && !pendingInitialization ? 'true' : 'false';
        button.dataset.trustpilotHadAriaDisabled = button.hasAttribute('aria-disabled') ? 'true' : 'false';
        button.dataset.trustpilotPreviousAriaDisabled = button.getAttribute('aria-disabled') || '';
        button.setAttribute('data-trustpilot-locked', '');
      }

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });
  };

  const unlockCheckout = () => {
    checkoutButtons().forEach((button) => {
      if (!button.hasAttribute('data-trustpilot-locked')) {
        if (button.hasAttribute('data-trustpilot-pending-initialization')) {
          button.removeAttribute('data-trustpilot-pending-initialization');
          button.disabled = false;
        }
        return;
      }

      button.removeAttribute('data-trustpilot-pending-initialization');
      button.disabled = button.dataset.trustpilotWasDisabled === 'true';

      if (button.dataset.trustpilotHadAriaDisabled === 'true') {
        button.setAttribute('aria-disabled', button.dataset.trustpilotPreviousAriaDisabled);
      } else {
        button.removeAttribute('aria-disabled');
      }

      button.removeAttribute('data-trustpilot-locked');
      delete button.dataset.trustpilotWasDisabled;
      delete button.dataset.trustpilotHadAriaDisabled;
      delete button.dataset.trustpilotPreviousAriaDisabled;
    });
  };

  const renderInstances = ({ message = '', retry = false } = {}) => {
    instances.forEach((instance) => {
      instance.render(desiredValue, saving, message, retry);
    });
  };

  const isThemeEditor = () =>
    window.Shopify?.designMode === true || document.documentElement.classList.contains('shopify-design-mode');

  const cartUpdateUrl = () => {
    const shopRoot = window.Shopify?.routes?.root || '/';
    const normalizedRoot = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
    return `${normalizedRoot}cart/update.js`;
  };

  const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  const updateCartAttribute = async (value) => {
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (attempt > 0) await wait(350);

      try {
        const response = await fetch(cartUpdateUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            attributes: {
              [ATTRIBUTE_KEY]: value,
            },
          }),
        });

        if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);

        const cart = await response.json();
        if (cart.attributes?.[ATTRIBUTE_KEY] !== value) {
          throw new Error('Cart attribute was not confirmed by Shopify');
        }

        return cart;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  };

  const flush = async () => {
    if (saving || confirmedValue === desiredValue) return;

    saving = true;
    initializationFailed = false;
    lockCheckout();
    renderInstances({ message: 'Auswahl wird gespeichert …' });

    if (isThemeEditor()) {
      confirmedValue = desiredValue;
      saving = false;
      unlockCheckout();
      renderInstances();
      return;
    }

    try {
      while (confirmedValue !== desiredValue) {
        const valueToSave = desiredValue;
        const cart = await updateCartAttribute(valueToSave);
        confirmedValue = valueToSave;
        cartToken = cart.token || cartToken;
      }

      saving = false;
      unlockCheckout();
      renderInstances();
    } catch (error) {
      console.error('Trustpilot invitation preference could not be saved.', error);
      saving = false;

      if (confirmedValue && VALID_VALUES.has(confirmedValue)) {
        desiredValue = confirmedValue;
        unlockCheckout();
        renderInstances({
          message: 'Die Auswahl konnte nicht gespeichert werden und wurde auf den zuletzt gespeicherten Stand zurückgesetzt.',
        });
      } else {
        initializationFailed = true;
        lockCheckout();
        renderInstances({
          message: 'Die Auswahl konnte noch nicht gespeichert werden. Bitte versuche es erneut.',
          retry: true,
        });
      }
    }
  };

  const setDesiredValue = (value) => {
    if (!VALID_VALUES.has(value)) return;

    desiredValue = value;
    initializationFailed = false;
    lockCheckout();
    renderInstances({ message: 'Auswahl wird gespeichert …' });
    flush();
  };

  const register = (instance) => {
    const renderedValue = instance.dataset.cartValue;
    const renderedCartToken = instance.dataset.cartToken || null;
    instances.add(instance);

    if (!initialized || (!saving && cartToken && renderedCartToken && cartToken !== renderedCartToken)) {
      confirmedValue = VALID_VALUES.has(renderedValue) ? renderedValue : null;
      desiredValue = confirmedValue || 'ja';
      cartToken = renderedCartToken;
      initialized = true;
      initializationFailed = false;
    }

    instance.render(desiredValue, saving, saving ? 'Auswahl wird gespeichert …' : '');

    if (confirmedValue !== desiredValue) {
      lockCheckout();
      flush();
    } else if (!saving && !initializationFailed) {
      unlockCheckout();
    }
  };

  const unregister = (instance) => {
    instances.delete(instance);
  };

  document.addEventListener(
    'submit',
    (event) => {
      if (!saving && !initializationFailed && confirmedValue === desiredValue) return;

      const submitter = event.submitter;
      const isTrustpilotCheckout =
        submitter?.matches('[data-trustpilot-checkout]') ||
        event.target.querySelector('[data-trustpilot-checkout]');

      if (isTrustpilotCheckout) {
        event.preventDefault();
        lockCheckout();
      }
    },
    true
  );

  class TrustpilotCartInvitation extends HTMLElement {
    connectedCallback() {
      if (this.isRegistered) return;

      this.checkbox = this.querySelector('[data-trustpilot-checkbox]');
      this.status = this.querySelector('[data-trustpilot-status]');
      this.retryButton = this.querySelector('[data-trustpilot-retry]');

      if (!this.checkbox || !this.status || !this.retryButton) return;

      this.onChange = () => setDesiredValue(this.checkbox.checked ? 'ja' : 'nein');
      this.onRetry = () => flush();
      this.checkbox.addEventListener('change', this.onChange);
      this.retryButton.addEventListener('click', this.onRetry);
      this.isRegistered = true;
      register(this);
    }

    disconnectedCallback() {
      if (!this.isRegistered) return;

      this.checkbox.removeEventListener('change', this.onChange);
      this.retryButton.removeEventListener('click', this.onRetry);
      this.isRegistered = false;
      unregister(this);
    }

    render(value, isSaving, message = '', showRetry = false) {
      this.checkbox.checked = value !== 'nein';
      this.dataset.cartValue = VALID_VALUES.has(confirmedValue) ? confirmedValue : '';
      this.setAttribute('aria-busy', isSaving ? 'true' : 'false');
      this.status.textContent = message;
      this.status.hidden = message === '';
      this.retryButton.hidden = !showRetry;
    }
  }

  customElements.define('trustpilot-cart-invitation', TrustpilotCartInvitation);
})();
